/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * «Bønnetidskilde» — the timetable as approved versions (prayer-time connector framework, phase 1).
 *
 * Shown on Bønnetider only when the mosque has the `timetable_source` capability and the person holds
 * timetable.source.read; what each person may DO comes from the server (`can`), never from a guess here.
 *
 * Rules this section keeps:
 *  - Nothing is published by uploading. The preview says what would change; publishing is a separate, two-step act,
 *    and a version with notes cannot be published until somebody ticks that they have looked.
 *  - A summary first (source, approved coverage, status, waiting), the versions as a short list, one version's detail
 *    beside it (a drawer at 900 px and up, the whole width below) — closable at every width, and by Escape; focus goes
 *    back to the row that opened it.
 *  - There is no Bønnetid fetch button and no key field: Bønnetid is shown as a source that is not available, and why.
 *  - Norwegian, English and Urdu (right to left), chosen here and remembered for this tab (sessionStorage — the panel keeps nothing in localStorage).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { post, readUpload, send, type Body } from '../lib/api';
import { useLoad, useMsg, useToastOn } from '../lib/hooks';
import { osloTime } from '../lib/format';
import { Btn, Card, DropZone, LoadError, Loading, Msg, Tag, TwoStep, type TagTone } from '../components/ui';
import { BackButton, useEscapeToClose } from './work-ui';
// Where the mosque stands with adoption, for the old Årstabell card below (Times.tsx), which words its note by it.
import type { AdoptionState } from '../lib/timetable-card-text';
import { LANGS, LANG_NAME, fieldText, itemText, refusalText, runwayText, t as tr, type Lang, type TextKey } from '../lib/timetable-source-text';

const BASE = '/timetable-source';
const STATUS_TONE: Record<string, TagTone> = { pending: 'gold', published: 'green', rejected: 'red', superseded: 'grey', rolled_back: 'grey' };
const FILTERS = [['pending', 'fPending'], ['published', 'fPublished'], ['rejected', 'fRejected'], ['all', 'fAll']] as const;
const LANG_KEY = 'oa.panel.timetableSource.lang';

function savedLang(): Lang {
  try { const v = sessionStorage.getItem(LANG_KEY); return (LANGS as string[]).includes(v || '') ? (v as Lang) : 'no'; } catch { return 'no'; }
}

export function TimetableSource({ onPublished, onAdoption }: { onPublished?: () => void; onAdoption?: (s: AdoptionState) => void }) {
  const [lang, setLang] = useState<Lang>(savedLang);
  const T = useCallback((k: TextKey) => tr(lang, k), [lang]);
  useEffect(() => { try { sessionStorage.setItem(LANG_KEY, lang); } catch { /* blocked storage: the choice lasts until the page reloads */ } }, [lang]);

  const status = useLoad<Body>(`${BASE}/status`);
  const [filter, setFilter] = useState<string>('pending');
  const [limit, setLimit] = useState(10);
  const list = useLoad<Body>(`${BASE}/versions?status=${filter}&limit=${limit}`, [filter, limit]);
  const [openId, setOpenId] = useState<string | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  const [note, say] = useMsg();
  const reloadAll = useCallback(async () => { await Promise.all([status.reload(), list.reload()]); }, [status, list]);
  // Closing the detail gives the focus back to the row that opened it — after React has removed the detail, so the row
  // is visible again (on a phone the list is hidden while a detail is open).
  const wasOpen = useRef(false);
  useEffect(() => {
    if (openId) { wasOpen.current = true; return; }
    if (wasOpen.current && opener.current && opener.current.isConnected) opener.current.focus();
    wasOpen.current = false;
  }, [openId]);

  const dir = lang === 'ur' ? 'rtl' : 'ltr';
  const shell = (body: React.ReactNode) => (
    <Card className="tts" id="tts-card">
      <div lang={lang} dir={dir} className={'tts-body' + (lang === 'ur' ? ' tts-ur' : '')}>
        <h3 id="tts-title">{T('title')}</h3>
        <p className="hint">{T('hint')}</p>
        <div className="tts-lang" role="group" aria-label={T('language')}>
          {LANGS.map((l) => (
            <button key={l} type="button" className="tts-lang-btn" aria-pressed={lang === l} lang={l} onClick={() => setLang(l)}>{LANG_NAME[l]}</button>
          ))}
        </div>
        {body}
      </div>
    </Card>
  );
  const st = status.data;
  const adoption: AdoptionState | null = !st ? null : st.unadopted ? 'unadopted' : (st.publishedAll || []).length ? 'published'
    : st.history && st.history.versions > 0 ? 'unpublished' : 'none';
  useEffect(() => { if (adoption && onAdoption) onAdoption(adoption); }, [adoption, onAdoption]);
  if (status.error) return shell(<LoadError error={status.error} retry={status.reload} />);
  if (!status.data) return shell(<Loading what={T('loading')} />);
  const s = status.data, can = s.can || {};
  const bonnetid = (s.providers || []).find((p: Body) => p.key === 'bonnetid');
  const close = () => setOpenId(null);

  return shell(<>
    <dl className="tts-summary">
      <div><dt>{T('source')}</dt><dd>{s.settings && s.settings.sourceType === 'upload' ? T('srcUpload') : s.settings && s.settings.sourceType}
        {bonnetid && !bonnetid.available && <span className="tts-sub">{T('srcBonnetid')}: <Tag tone="grey">{T('unavailable')}</Tag></span>}</dd></div>
      <div><dt>{T('coverage')}</dt><dd>{(s.publishedAll || []).length ? (s.publishedAll as Body[]).map((p, i) => <React.Fragment key={p.id}>{i > 0 && ', '}<bdi>{p.coverage.from} – {p.coverage.to}</bdi></React.Fragment>) : T('noneApproved')}
        {s.published && s.published.publishedAt && <span className="tts-sub">{T('publishedAt')} {osloTime(s.published.publishedAt)}</span>}</dd></div>
      <div><dt>{T('freshness')}</dt><dd><Tag tone={s.status.state === 'ok' ? 'green' : s.status.state === 'warning' ? 'gold' : 'red'}>
        <bdi>{runwayText(lang, s.status.runway, s.status.lastDate)}</bdi></Tag></dd></div>
      <div><dt>{T('waiting')}</dt><dd><b>{s.status.pending}</b></dd></div>
    </dl>
    {(s.status.diverged || []).length > 0 && <p className="msg warn tts-diverged" role="status">{T('diverged')}{' '}
      {(s.status.diverged as Body[]).map((x) => <bdi key={x.versionId}>({x.from} – {x.to}: {x.dates.slice(0, 8).join(', ')}{x.count > 8 ? ' …' : ''})</bdi>)}</p>}
    {bonnetid && !bonnetid.available && <p className="note tts-why">{T('bonnetidWhy')}</p>}
    {/* History, but nothing approved (an adoption or uploads rejected): say what members see. */}
    {s.history && s.history.versions > 0 && !(s.publishedAll || []).length && s.status.lastDate && <p className="note tts-history-none">{T('historyNoneApproved')}</p>}

    {s.unadopted && <section className="tts-unadopted" aria-labelledby="tts-unadopted-title">
      <h4 id="tts-unadopted-title">{T('unadopted')}</h4>
      <p className="note"><bdi>{s.unadopted.from} – {s.unadopted.to}</bdi> · {s.unadopted.days} {T('days')}</p>
      {s.unadopted.earlier && <p className="note tts-earlier">{T('adoptEarlier')} <bdi>{s.unadopted.earlier.from} – {s.unadopted.earlier.to}</bdi> · {s.unadopted.earlier.days} {T('days')}</p>}
      {s.unadopted.later && <p className="note tts-later">{T('adoptLater')} <bdi>{s.unadopted.later.from} – {s.unadopted.later.to}</bdi> · {s.unadopted.later.days} {T('days')}</p>}
      {s.unadopted.tooLarge ? <p className="note tts-too-large">{T('adoptTooLarge')}</p>
        : can.adopt ? <><p className="note tts-adopt-first">{T('adoptFirst')}</p><AdoptCurrent T={T} lang={lang} onAdopted={async (id) => { await reloadAll(); setFilter('pending'); setOpenId(id); }} /></>
        : <p className="note">{T('noPermission')}</p>}
    </section>}
    {s.onlyEarlier && <section className="tts-unadopted" aria-labelledby="tts-only-earlier-title">
      <h4 id="tts-only-earlier-title">{T('onlyEarlier')}</h4>
      <p className="note"><bdi>{s.onlyEarlier.from} – {s.onlyEarlier.to}</bdi> · {s.onlyEarlier.days} {T('days')}</p>
    </section>}

    {can.upload ? <Upload T={T} lang={lang} mosqueAsr={s.asr && s.asr.mosque} onUploaded={async (id) => { await reloadAll(); setFilter('pending'); setOpenId(id); }} />
      : <p className="note">{T('noPermission')}</p>}
    <Msg text={note.text} tone={note.tone} />

    <div className={'work-split' + (openId ? ' has-detail' : '')}>
      <div className="work-list">
        <div className="tts-filter" role="group" aria-label={T('filter')}>
          {FILTERS.map(([k, label]) => (
            <button key={k} type="button" className="tts-filter-btn" aria-pressed={filter === k} onClick={() => { setFilter(k); setLimit(10); }}>{T(label)}</button>
          ))}
        </div>
        {list.error ? <LoadError error={list.error} retry={list.reload} />
          : !list.data ? <Loading what={T('loading')} />
          : !(list.data.items || []).length ? <p className="note tts-empty">{filter === 'pending' ? T('emptyPending') : T('empty')}</p>
          : <ul className="work-rows tts-rows">
              {list.data.items.map((v: Body) => (
                <li key={v.id}>
                  <button type="button" className={'work-row' + (openId === v.id ? ' on' : '')} aria-current={openId === v.id || undefined}
                    onClick={(e) => { opener.current = e.currentTarget; setOpenId(v.id); }}>
                    <span className="tts-row-head"><bdi>{v.coverage.from} – {v.coverage.to}</bdi> <Tag tone={STATUS_TONE[v.status] || 'grey'}>{T(('st_' + v.status) as TextKey)}</Tag></span>
                    <span className="note">{v.coverage.days} {T('days')} · {v.counts.added} {T('added')} · {v.counts.changed} {T('changed')}
                      {v.counts.warnings > 0 && <> · <b>{v.counts.warnings} {T('warnings')}</b></>}
                      {v.fileName && <> · <bdi>{v.fileName}</bdi></>}</span>
                  </button>
                </li>
              ))}
            </ul>}
        {list.data && list.data.total > (list.data.items || []).length && <Btn ghost onClick={() => setLimit((n) => Math.min(n + 10, 50))}>{T('more')}</Btn>}
      </div>
      {openId && <Detail key={openId} id={openId} T={T} lang={lang} can={can} onClose={close}
        onChanged={async (text, tone, published) => { say(text, tone); await reloadAll(); if (published && onPublished) onPublished(); }} />}
    </div>
  </>);
}

/** Turn an already visible timetable into a pending first version; it never publishes or changes a prayer_day row. */
function AdoptCurrent({ T, lang, onAdopted }: { T: (k: TextKey) => string; lang: Lang; onAdopted: (id: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<{ text: string; lang: Lang } | null>(null);
  useToastOn(refused && refused.text, 'err');
  const [note, say] = useMsg();
  const adopt = async () => {
    setBusy(true); setRefused(null); say('');
    try {
      const r = await post(`${BASE}/adopt-current`, {});
      if (!r.ok) { setRefused(refusalText(lang, r.body)); return; }
      say(T('adopted'), 'ok'); onAdopted(r.body.version.id);
    } catch (e) { setRefused({ text: (e as Error).message || 'Det gikk ikke.', lang: 'no' }); }
    finally { setBusy(false); }
  };
  return <div className="tts-adopt">
    <p className="note">{T('adoptCurrentNote')}</p>
    <TwoStep ghost={false} label={T('adoptCurrent')} confirmLabel={T('adoptCurrent')} disabled={busy} onConfirm={adopt} />
    <Msg text={note.text} tone={note.tone} />
    {refused && <p className="msg err tts-refused" role="alert" lang={refused.lang}>{refused.text}</p>}
  </div>;
}

function Upload({ T, lang, mosqueAsr, onUploaded }: { T: (k: TextKey) => string; lang: Lang; mosqueAsr: string | null; onUploaded: (id: string) => void }) {
  const [asr, setAsr] = useState<string>(mosqueAsr || '');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<Body | null>(null);
  const [refused, setRefused] = useState<{ text: string; lang: Lang } | null>(null);
  useToastOn(refused && refused.text, 'err');
  const [note, say] = useMsg();
  const input = useRef<HTMLInputElement>(null);
  async function onFile(file: File) {
    setRefusal(null); setRefused(null); say(T('reading'));
    setBusy(true);
    try {
      const body = await readUpload(file);
      const r = await post(`${BASE}/uploads`, { ...body, ...(asr ? { asrMethod: asr } : {}) });
      if (!r.ok) {
        setRefusal(r.body);
        // A refusal said in the chosen language — or, for a code this section does not know, the server's Norwegian, marked so.
        if (r.body && r.body.code === 'invalid_file') say(T('refused'), 'err'); else { say(''); setRefused(refusalText(lang, r.body)); }
        return;
      }
      say('');
      onUploaded(r.body.version.id);
    } catch (e) { say((e as Error).message, 'err'); }
    finally { setBusy(false); if (input.current) input.current.value = ''; }
  }
  return (
    <div className="tts-upload">
      <div className="field tts-asr">
        <label htmlFor="tts-asr">{T('asr')}</label>
        <select id="tts-asr" value={asr} onChange={(e) => setAsr(e.target.value)} aria-describedby="tts-asr-note" disabled={busy}>
          <option value="">{T('asrChoose')}</option>
          <option value="standard">{T('asrStandard')}</option>
          <option value="hanafi">{T('asrHanafi')}</option>
        </select>
        <span className="note" id="tts-asr-note">{T('asrNote')}</span>
      </div>
      <DropZone title={T('uploadTitle')} accept=".xlsx,.csv,.txt" onFile={onFile} inputRef={input}>
        <span className="note">{T('uploadNote')}</span>
      </DropZone>
      <Msg text={note.text} tone={note.tone} />
      {refused && <p className="msg err tts-refused" role="alert" lang={refused.lang}>{refused.text}</p>}
      {refusal && Array.isArray(refusal.errors) && refusal.errors.length > 0 && (
        <div className="tts-errors" role="alert">
          <h4>{T('errorsTitle')}</h4>
          <ul>{refusal.errors.slice(0, 15).map((e: Body, i: number) => { const x = itemText(lang, e);
            return <li key={i}>{e.row ? <bdi>#{e.row}: </bdi> : null}{x.head}{x.detail && <span className="note" lang="no"> — {x.detail}</span>}</li>; })}</ul>
        </div>
      )}
    </div>
  );
}

function Detail({ id, T, lang, can, onClose, onChanged }: {
  id: string; T: (k: TextKey) => string; lang: Lang; can: Body; onClose: () => void;
  onChanged: (text: string, tone: 'ok' | 'err' | 'warn' | '', published?: boolean) => Promise<void>;
}) {
  const { data, error, reload } = useLoad<Body>(`${BASE}/versions/${encodeURIComponent(id)}`);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, say] = useMsg();
  const anchor = useRef<HTMLHeadingElement>(null);
  useEscapeToClose(anchor as React.RefObject<HTMLElement>, onClose, busy);
  useEffect(() => { if (data && anchor.current) anchor.current.focus(); }, [Boolean(data)]);

  // A refusal the server explains: said in the chosen language by its code (or, unknown, in the server's Norwegian,
  // marked as such), with the days it names — a rollback stopped over changed or overridden days lists them.
  const [refused, setRefused] = useState<{ text: string; lang: Lang; dates: string[] } | null>(null);
  useToastOn(refused && refused.text, 'err');
  const act = async (path: string, payload: Body, ok: string, published = false) => {
    setBusy(true); setRefused(null); say('');
    try {
      const r = await send(`${BASE}/versions/${encodeURIComponent(id)}/${path}`, { method: 'POST', body: JSON.stringify(payload) });
      if (r.ok) { setAck(false); await onChanged(ok, 'ok', published); await reload(); return; }
      if (r.body && r.body.code === 'version_conflict') { await onChanged(T('conflict'), 'warn'); await reload(); return; }
      if (r.body && r.body.code === 'acknowledgement_required') { say(T('ackNeeded'), 'err'); return; }
      if (r.body && r.body.code === 'stale_preview') { say(T('stale'), 'err'); await reload(); return; }
      if (r.body && r.body.code === 'cannot_approve_own') { say(T('ownUpload'), 'err'); return; }
      setRefused(refusalText(lang, r.body));
    } catch (e) {
      setRefused({ text: (e as Error).message || 'Det gikk ikke.', lang: 'no', dates: [] });
    } finally {
      setBusy(false);   // always: a failed request never leaves the buttons switched off
    }
  };

  const box = (body: React.ReactNode) => (
    <Card className="work-detail tts-detail" titleId="tts-detail-title" title={T('detail')}>
      <div className="row"><BackButton onClick={onClose} what={T('detail')} /></div>
      {body}
    </Card>
  );
  if (error) return box(<LoadError error={error} retry={reload} />);
  if (!data) return box(<Loading what={T('loading')} />);
  const v = data.version, val = data.validation || {}, diff = data.diff;
  const warnings: Body[] = val.warnings || [], notes: Body[] = val.notes || [];
  const pending = v.status === 'pending';
  const own = pending && data.canApprove === false;
  return box(<>
    <h4 tabIndex={-1} ref={anchor} className="tts-detail-head"><bdi>{v.coverage.from} – {v.coverage.to}</bdi>{' '}
      <Tag tone={STATUS_TONE[v.status] || 'grey'}>{T(('st_' + v.status) as TextKey)}</Tag></h4>
    <p className="note">{v.coverage.days} {T('days')}{v.fileName && <> · <bdi>{v.fileName}</bdi></>}{v.asrMethod && <> · {T('asr')}: {v.asrMethod === 'hanafi' ? T('asrHanafi') : T('asrStandard')}</>}</p>
    <p className="note">{T('uploadedBy')} {osloTime(v.uploadedAt)}{v.uploadedByName && <> {T('by')} <bdi>{v.uploadedByName}</bdi></>}
      {v.decidedAt && <> · {T('decidedBy')} {osloTime(v.decidedAt)}{v.decidedByName && <> {T('by')} <bdi>{v.decidedByName}</bdi></>}</>}</p>
    {data.stale && <p className="msg err" role="alert">{T('stale')}</p>}
    {data.diverged && data.diverged.count > 0 && <p className="msg warn tts-diverged" role="status">{T('diverged')}{' '}
      <bdi>({data.diverged.dates.slice(0, 8).join(', ')}{data.diverged.count > 8 ? ' …' : ''})</bdi></p>}

    {notes.length > 0 && <section aria-labelledby="tts-notes"><h4 id="tts-notes">{T('notesTitle')}</h4>
      <ul className="tts-list">{notes.map((x, i) => { const w = itemText(lang, x); return <li key={i}>{w.head}{w.detail && <span className="note" lang="no"> — {w.detail}</span>}</li>; })}</ul></section>}
    {warnings.length > 0 && <section aria-labelledby="tts-warn"><h4 id="tts-warn" className="warnc">{T('warningsTitle')}</h4>
      <ul className="tts-list">{warnings.map((x, i) => { const w = itemText(lang, x);
        return <li key={i}>{w.head}{w.detail && <span className="note" lang="no"> — {w.detail}</span>}
          {Array.isArray(x.dates) && x.dates.length > 0 && <span className="note"> (<bdi>{x.dates.slice(0, 8).join(', ')}{x.dates.length > 8 ? ' …' : ''}</bdi>)</span>}</li>; })}</ul>
      {(val.suspect || []).length > 0 && <ul className="tts-list tts-suspect">{val.suspect.slice(0, 10).map((x: Body) => (
        <li key={x.date}><bdi>{x.date}</bdi>: <span lang="no">{x.reason_no}</span></li>))}</ul>}
    </section>}

    {diff && <section aria-labelledby="tts-diff"><h4 id="tts-diff">{T('diffTitle')}</h4>
      <p><b>{diff.added}</b> {T('added')} · <b>{diff.changed}</b> {T('changed')} · {diff.unchanged} {T('unchanged')}</p>
      {diff.added + diff.changed === 0 ? <p className="note">{T('diffNone')}</p> : (
        <>{diff.days.some((d: Body) => (d.inherited || []).length > 0) && <p className="note tts-kept-why" id="tts-kept-why">{T('keptTitle')}</p>}
        <div className="tts-diff-wrap"><table className="t tts-diff" aria-describedby={diff.days.some((d: Body) => (d.inherited || []).length > 0) ? 'tts-kept-why' : undefined}>
          <caption className="tts-cap">{T('diffCaption')}</caption>
          <thead><tr><th scope="col">{T('colDate')}</th><th scope="col">{T('colChange')}</th></tr></thead>
          <tbody>{diff.days.slice(0, 40).map((d: Body) => (
            <tr key={d.date} className={d.override ? 'tts-ov' : undefined}><th scope="row"><bdi>{d.date}</bdi>{d.override && <span className="tts-ov-tag"><Tag tone="gold">{T('overrideDay')}</Tag>{' '}
              <span className="note">{(d.overrideFields || []).map((f: string) => fieldText(lang, f)).join(', ')}</span></span>}</th>
              <td>{d.status === 'added' ? T('dayAdded') : (d.fields || []).map((f: Body) => <span key={f.field} className="tts-chg">{fieldText(lang, f.field)}: <bdi>{f.from || '—'} → {f.to || '—'}</bdi></span>)}
                {(d.inherited || []).length > 0 && <span className="tts-kept">{T('kept')}: {(d.inherited as Body[]).map((k) => <span key={k.field} className="tts-kept-one">{fieldText(lang, k.field)} <bdi>{k.value}</bdi></span>)}</span>}</td></tr>
          ))}</tbody>
        </table>{(diff.truncated || diff.days.length > 40) && <p className="note">{T('truncated')}</p>}</div></>
      )}
    </section>}

    <Msg text={note.text} tone={note.tone} />
    {refused && <div className="msg err tts-refused" role="alert" lang={refused.lang}>{refused.text}
      {refused.dates.length > 0 && <> <span lang={lang}>{T('refusedDays')}:</span> <bdi>{refused.dates.slice(0, 31).join(', ')}</bdi></>}</div>}
    {pending && can.approve && <div className="tts-actions">
      {/* Nobody approves their own upload: the server refuses it (cannot_approve_own); here it is not offered. */}
      {own && <p className="note tts-own">{T('ownUpload')}</p>}
      {!own && warnings.length > 0 && <label className="tts-ack"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} /> {T('ack')}</label>}
      <div className="row">
        {!own && <TwoStep ghost={false} label={T('approve')} confirmLabel={T('approveConfirm')} disabled={busy || data.stale || (warnings.length > 0 && !ack)}
          onConfirm={() => act('approve', { acknowledgeWarnings: ack, version: v.version }, T('approved'), true)} />}
        <TwoStep label={T('reject')} confirmLabel={T('rejectConfirm')} disabled={busy} onConfirm={() => act('reject', { version: v.version }, T('rejectedMsg'))} />
      </div>
    </div>}
    {v.status === 'published' && can.rollback && <div className="tts-actions row">
      <TwoStep label={T('rollback')} confirmLabel={T('rollbackConfirm')} disabled={busy} onConfirm={() => act('rollback', { version: v.version }, T('rolledBack'), true)} />
    </div>}
  </>);
}
